import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import type {
  Eval2RunsStatusResponse,
  DevAreaEvalsResponse,
  DevAreaEvalDetail,
} from './types';

// Atom to store the selected hours for lookback
export const hoursAtom = atom(24);

// Atom to store a specific evaluation ID to query (null means use hours filter)
export const evalIdAtom = atom<string | null>(null);

// Atom to store a specific package ID to filter by
export const packageIdAtom = atom<string | null>(null);

// Refresh trigger atom - increment to refetch data
const refreshCounterAtom = atom(0);

// Base async atom that depends on hoursAtom, evalIdAtom, packageIdAtom, and refresh counter
const eval2RunsStatusBaseAtom = atom(async (get) => {
  const hours = get(hoursAtom);
  const evalId = get(evalIdAtom);
  const packageId = get(packageIdAtom);
  get(refreshCounterAtom); // Creates dependency for manual refetch

  const params = new URLSearchParams();
  if (evalId) {
    params.set('id', evalId);
  } else {
    params.set('hours', String(hours));
    if (packageId) {
      params.set('package_id', packageId);
    }
  }

  const response = await request.get<Eval2RunsStatusResponse>(
    `/admin/eval2_runs_status?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

// Loadable wrapper for loading/error states
const eval2RunsStatusLoadableAtom = loadable(eval2RunsStatusBaseAtom);

// Combined atom that provides loadable state and refetch capability
export const eval2RunsStatusAtom = atom(
  (get) => get(eval2RunsStatusLoadableAtom),
  (_get, set) => {
    set(refreshCounterAtom, (c) => c + 1);
  }
);

// Cache for expanded row data (keyed by package evaluation ID)
export const devAreaEvalsCacheAtom = atom<Record<number, DevAreaEvalDetail[]>>(
  {}
);

// Fetch dev area evaluations for a specific package evaluation
export const fetchDevAreaEvals = async (
  packageEvaluationId: number
): Promise<DevAreaEvalDetail[]> => {
  const response = await request.get<DevAreaEvalsResponse>(
    `/admin/eval2_runs_status/${packageEvaluationId}/development_area_evaluations`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response.data;
};

// Retry a pending dev area evaluation synchronously
export const retryDevAreaEval = async (
  pedaeId: number
): Promise<{ success: boolean; error?: string }> => {
  const controller = new AbortController();
  const response = await request.post<
    { success: true; id: number },
    { error: string }
  >(`/admin/eval2_runs_status/${pedaeId}/retry_pending`, {}, controller);

  if (response.type === 'success') {
    return { success: true };
  }
  if (response.type === 'error') {
    return { success: false, error: response.data?.error ?? 'Unknown error' };
  }
  return { success: false, error: response.message ?? 'Request failed' };
};
